import * as z from 'zod/v4';

/**
 * Extract string path keys to an array of separate keys
 * Eg "key1.key2" => ["key1", "key2"].
 *
 * @param path path to extract
 * @returns Extracted keys
 */
const stringToPathArray = (path = ''): (string | number | undefined)[] => {
  if (path.length === 0) {
    return [];
  }

  // Regex is used to match strings that start with a [, followed by any characters, and end with a ],
  // capturing the content inside the brackets and any remaining characters after the brackets.
  // Eg.
  // [example]rest would match, capturing example and rest.
  // [data]moreData would match, capturing data and moreData.
  const bracketMatching = /^\[(.+?)](.*)$/;

  // Regex is used to match strings that may start with an optional dot,
  // followed by any characters except ., [, or ], capturing these characters and any remaining characters after them.
  // Eg.
  // .example.rest would match, capturing example and .rest.
  // data.moreData would match, capturing data and .moreData.

  const dotNotationMatching = /^\.?([^.[\]]+)(.*)$/;

  const match = path.match(bracketMatching) ?? path.match(dotNotationMatching);

  const [, key, rest] = match ?? [];
  if (key) {
    const aKey = /^\d+$/.test(key) ? Number(key) : key;

    return [aKey, ...stringToPathArray(rest)];
  }

  return [path];
};

/**
 * Expands an object to the shape of the given path.
 *
 * @param object Object to expand
 * @param path The path to expand object to
 * @param value Value to set to the expanded path
 * @returns expanded object
 */
function setPath(
  object: Record<string | number, unknown>,
  path: string | string[],
  value: unknown
): Record<string | number, unknown> {
  const parts = Array.isArray(path) ? path : stringToPathArray(path);
  let obj = object;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const nextPart = parts[i + 1];

    if (part === undefined) {
      continue;
    }

    if (obj[part] === void 0) {
      obj[part] = typeof nextPart === 'number' ? [] : {};
    }
    // obj = obj[part];
    obj = obj[part] as Record<string | number, unknown>;
  }

  const key = parts.at(-1);
  if (key !== undefined) {
    obj[key] = value;
  }

  return object;
}

type InputType<TDefaultSchema extends z.ZodType> = {
  (): z.ZodType<z.infer<TDefaultSchema>>;
  <TProvidedSchema extends z.ZodType>(schema: TProvidedSchema): z.ZodType<z.infer<TProvidedSchema>>;
};

const stripEmpty = z.literal('').transform(() => undefined);

const preprocessIfValid =
  (schema: z.ZodType) =>
  (val: unknown): unknown => {
    const result = schema.safeParse(val);
    if (result.success) {
      return result.data;
    }
    return val;
  };

/**
 * Transforms any empty strings to `undefined` before validating.
 * This makes it so empty strings will fail required checks,
 * allowing you to use `optional` for optional fields instead of `nonempty` for required fields.
 * If you call `zfd.text` with no arguments, it will assume the field is a required string by default.
 * If you want to customize the schema, you can pass that as an argument.
 *
 * @param schema Zod schema
 * @returns preprocessed zod schema
 */
// eslint-disable-next-line no-restricted-syntax
export const text: InputType<z.ZodString> = (schema = z.string()) =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
  z.preprocess(preprocessIfValid(stripEmpty), schema) as any;

/**
 * Coerces numerical strings to numbers transforms empty strings to `undefined` before validating.
 * If you call `zfd.number` with no arguments,
 * it will assume the field is a required number by default.
 * If you want to customize the schema, you can pass that as an argument.
 *
 * @param schema Zod schema
 * @returns preprocessed zod schema
 */
// eslint-disable-next-line no-restricted-syntax
export const numeric: InputType<z.ZodNumber> = (schema = z.number()) =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  z.preprocess(
    preprocessIfValid(
      z.union([
        stripEmpty,
        z
          .string()
          .transform(Number)
          .refine((val) => !Number.isNaN(val)),
      ])
    ),
    schema
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as any;

type CheckboxOpts = {
  trueValue?: string;
};

/**
 * Turns the value from a checkbox field into a boolean,
 * but does not require the checkbox to be checked.
 * For checkboxes with a `value` attribute, you can pass that as the `trueValue` option.
 *
 * @example
 * ```ts
 * const schema = zfd.formData({
 *   defaultCheckbox: zfd.checkbox(),
 *   checkboxWithValue: zfd.checkbox({ trueValue: "true" }),
 *   mustBeTrue: zfd
 *     .checkbox()
 *     .refine((val) => val, "Please check this box"),
 *   });
 * });
 * ```
 * @param root0 Checkbox options
 * @param root0.trueValue Specify the string 'true value'
 * @returns preprocessed zod schema
 */
// ZodEffects<ZodTypeAny>
// eslint-disable-next-line no-restricted-syntax
export const checkbox = ({ trueValue = 'on' }: CheckboxOpts = {}): z.ZodUnion<
  [z.ZodType<boolean>, z.ZodType<boolean>]
> => z.union([z.literal(trueValue).transform(() => true), z.literal(undefined).transform(() => false)]);

/**
 * Schema for file upload.
 *
 * @param schema Zod schema
 * @returns processed zod schema
 */
// eslint-disable-next-line no-restricted-syntax
export const file: InputType<z.ZodType<File>> = (schema = z.instanceof(File)) =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  z.preprocess((val) => {
    // Empty File object on no user input, so convert to undefined
    return val instanceof File && val.size === 0 ? undefined : val;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, schema) as any;

/**
 * Preprocesses a field where you expect multiple values could be present for the same field name
 * and transforms the value of that field to always be an array.
 * If you don't provide a schema, it will assume the field is an array of zfd.text fields
 * and will not require any values to be present.
 *
 * @param schema Zod schema for repeatable types like checkbox group
 * @returns Zod schema array
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any, no-restricted-syntax
export const repeatable: InputType<z.ZodArray<any>> = (schema = z.array(text())) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return z.preprocess((val) => {
    if (Array.isArray(val)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return val;
    }
    if (val === undefined) {
      return [];
    }
    return [val];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, schema) as any;
};

/**
 * A convenience wrapper for repeatable.
 * Instead of passing the schema for an entire array, you pass in the schema for the item type.
 *
 * @param schema Zod schema
 * @returns Zod schema array
 */
// eslint-disable-next-line no-restricted-syntax
export const repeatableOfType = <TType extends z.ZodType>(schema: TType): z.ZodType<z.infer<z.ZodArray<TType>>> =>
  repeatable(z.array(schema));

// const entries = z.array(z.tuple([z.string(), z.any()]));
const entries = z.array(z.tuple([z.string(), z.unknown()]));

type FormDataType = {
  <TShape extends z.ZodRawShape>(shape: TShape): z.ZodType<z.output<z.ZodObject<TShape>>>;
  <TShape extends z.ZodType>(schema: TShape): z.ZodType<z.output<TShape>>;
};

const safeParseJson = (jsonString: string): unknown => {
  try {
    return JSON.parse(jsonString) as unknown;
  } catch {
    return jsonString;
  }
};

/**
 * Parses given schema to json object.
 *
 * @param schema Zod schema
 * @returns JSON data
 */
// eslint-disable-next-line no-restricted-syntax
export const json = <TSchema extends z.ZodType>(schema: TSchema): z.ZodType<z.infer<TSchema>> =>
  z.preprocess(preprocessIfValid(z.union([stripEmpty, z.string().transform((val) => safeParseJson(val))])), schema);

const processFormData = preprocessIfValid(
  // We're avoiding using `instanceof` here because different environments
  // won't necessarily have `FormData` or `URLSearchParams`
  z
    .any()
    .refine((val) => Symbol.iterator in val)
    // Accepts both iterable (FormData, URLSearchParams) and plain objects
    .transform((val) => {
      if (Symbol.iterator in val) {
        // Iterable, e.g. FormData, URLSearchParams
        return [...(val as Iterable<[string, unknown]>)] as [string, unknown][];
      }
      // Plain object
      return Object.entries(val as Record<string, unknown>);
    })
    .refine((val): val is z.infer<typeof entries> => entries.safeParse(val).success)
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    .transform((data): Record<string, unknown | unknown[]> => {
      const map = new Map<string, unknown[]>();
      for (const [key, value] of data) {
        const stringKey = key;
        if (map.has(stringKey)) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          map.get(stringKey)!.push(value);
        } else {
          map.set(stringKey, [value]);
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
      return [...map.entries()].reduce<Record<string, unknown | unknown[]>>((acc, [key, value]) => {
        return setPath(acc, key, value.length === 1 ? value[0] : value);
      }, {});
    })
);

export const preprocessFormData = processFormData as (formData: unknown) => Record<string, unknown>;

/**
 * This helper takes the place of the `z.object` at the root of your schema.
 * It wraps your schema in a `z.preprocess` that extracts all the data out of a `FormData`
 * and transforms it into a regular object.
 * If the `FormData` contains multiple entries with the same field name,
 * it will automatically turn that field into an array.
 *
 * @param shapeOrSchema Shape or schema to validate
 * @returns New schema used for validation
 */
// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/no-explicit-any
export const formData: FormDataType = (shapeOrSchema: any): any =>
  z.preprocess(processFormData, shapeOrSchema instanceof z.ZodType ? shapeOrSchema : z.object(shapeOrSchema));
